import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Copy,
  Dices,
  Gamepad2,
  Link2,
  LockKeyhole,
  MessageCircle,
  Mic,
  MicOff,
  Plus,
  QrCode,
  Send,
  Sparkles,
  Trophy,
  Users,
  Volume2,
  VolumeX,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { tr } from "./i18n";
import type { LobbySettings, LobbyView, ServerEvent } from "../shared/protocol";
import "./styles.css";
import { Admin } from "./Admin";

type Screen = "home" | "browse" | "create" | "lobby";
type PublicLobby = {
  code: string;
  category_name: string;
  max_players: number;
  current_players: number;
};
const safeName = (value: string) =>
  value
    .replace(/[<>{}[\]\\]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 18);
const validName = (value: string) => safeName(value).length >= 2;
const codeClean = (value: string) =>
  value
    .toUpperCase()
    .replace(/[^A-HJ-NP-Z2-9]/g, "")
    .slice(0, 5);

function App() {
  const [screen, setScreen] = useState<Screen>("home");
  const [liveCount, setLiveCount] = useState(489);
  const [name, setName] = useState(localStorage.getItem("mm-name") ?? "");
  const [code, setCode] = useState(
    new URLSearchParams(location.search).get("lobi") ?? "",
  );
  const [error, setError] = useState("");
  const [sound, setSound] = useState(
    localStorage.getItem("mm-sound") !== "off",
  );
  const [lobby, setLobby] = useState<LobbyView | null>(null);
  const [selfId, setSelfId] = useState("");
  const [, setConnected] = useState(false);
  const ws = useRef<WebSocket | null>(null);
  const resumeAttempted = useRef(false);
  const [settings, setSettings] = useState<LobbySettings>({
    rounds: 3,
    captionSeconds: 90,
    voteSeconds: 25,
    maxPlayers: 8,
    profanityFilter: true,
    earlyFinish: true,
    lateJoin: false,
    spectatorMode: false,
    templateMode: "random",
    templateChoices: 5,
    soundEffects: true,
    adultSounds: false,
    isPrivate: false,
  });
  const [categories, setCategories] = useState<
    Array<{ id: number; name: string; meme_count: number }>
  >([]);
  const [publicLobbies, setPublicLobbies] = useState<PublicLobby[]>([]);
  const [pendingConfirm, setPendingConfirm] = useState<
    { kind: "leave"; nextScreen: Screen } | { kind: "close" } | null
  >(null);

  useEffect(() => {
    let visitorId=localStorage.getItem("mm-visitor-id");
    if(!visitorId){visitorId=crypto.randomUUID();localStorage.setItem("mm-visitor-id",visitorId);}
    void fetch("/api/analytics/visit",{method:"POST",headers:{"content-type":"application/json"},body:JSON.stringify({visitorId})}).catch(()=>undefined);
    const loadLiveCount=()=>fetch("/api/live-count").then(r=>r.ok?r.json():{count:489}).then((d:{count?:number})=>setLiveCount(d.count??489)).catch(()=>undefined);
    void loadLiveCount();
    const liveCountTimer=window.setInterval(loadLiveCount,300000);
    fetch("/api/categories")
      .then((r) => (r.ok ? r.json() : []))
      .then(setCategories)
      .catch(() => setCategories([]));
    const loadLobbies = () =>
      fetch("/api/lobbies")
        .then((r) => (r.ok ? r.json() : []))
        .then(setPublicLobbies)
        .catch(() => setPublicLobbies([]));
    void loadLobbies();
    const lobbyTimer = window.setInterval(loadLobbies, 5000);
    const initialCode = codeClean(
      new URLSearchParams(location.search).get("lobi") ?? "",
    );
    const hasSession =
      initialCode.length === 5 &&
      Boolean(localStorage.getItem(`mm-session-${initialCode}`));
    if (!hasSession) {
      const endpoint =
        initialCode.length === 5
          ? `/api/lobbies/${initialCode}?randomName=1`
          : "/api/player-name";
      fetch(endpoint)
        .then((r) => (r.ok ? r.json() : {}))
        .then((data: { name?: string; suggestedName?: string }) =>
          setName(data.suggestedName ?? data.name ?? ""),
        )
        .catch(() => undefined);
    }
    return () => {
      window.clearInterval(lobbyTimer);
      window.clearInterval(liveCountTimer);
      ws.current?.close();
    };
  }, []);
  const me = lobby?.players.find((p) => p.id === selfId);
  const toggleSound = () => {
    setSound((v) => {
      localStorage.setItem("mm-sound", String(!v));
      return !v;
    });
  };
  const connect = (roomCode: string, playerName: string, token?: string) => {
    const normalizedCode = codeClean(roomCode);
    setCode(normalizedCode);
    const pageUrl = new URL(location.href);
    pageUrl.searchParams.set("lobi", normalizedCode);
    history.replaceState(null, "", pageUrl);
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(
      `${protocol}//${location.host}/ws/${normalizedCode}?name=${encodeURIComponent(playerName)}${token ? `&token=${token}` : ""}`,
    );
    ws.current = socket;
    let receivedSnapshot = false;
    const timeout = window.setTimeout(() => {
      if (!receivedSnapshot) {
        socket.close();
        setError("Lobiye bağlanılamadı. Lobi kodunu kontrol edip tekrar dene.");
      }
    }, 8000);
    socket.onopen = () => setConnected(true);
    socket.onerror = () =>
      setError("Lobiye bağlanılamadı. Bağlantını ve lobi kodunu kontrol et.");
    socket.onclose = () => {
      setConnected(false);
      window.clearTimeout(timeout);
      if (!receivedSnapshot)
        setError("Lobi bulunamadı, dolu veya artık aktif değil.");
    };
    socket.onmessage = (event) => {
      const msg = JSON.parse(event.data) as ServerEvent;
      if (msg.type === "SNAPSHOT") {
        receivedSnapshot = true;
        window.clearTimeout(timeout);
        if (msg.sessionToken)
          localStorage.setItem(`mm-session-${normalizedCode}`, msg.sessionToken);
        setLobby(msg.lobby);
        setSelfId(msg.selfId);
        setScreen("lobby");
      }
      if (msg.type === "ERROR") setError(msg.message);
      if (msg.type === "LOBBY_CLOSED") {
        localStorage.removeItem(`mm-session-${normalizedCode}`);
        setError(msg.message);
        setLobby(null);
        setSelfId("");
        setScreen("home");
        const pageUrl = new URL(location.href);
        pageUrl.searchParams.delete("lobi");
        history.replaceState(null, "", pageUrl);
      }
      if (msg.type === "VOICE_SIGNAL")
        window.dispatchEvent(new CustomEvent("mm-voice-signal", { detail: msg }));
    };
  };
  useEffect(() => {
    if (resumeAttempted.current) return;
    resumeAttempted.current = true;
    const roomCode = codeClean(code),
      playerName = safeName(name),
      token =
        roomCode.length === 5
          ? localStorage.getItem(`mm-session-${roomCode}`)
          : null;
    if (roomCode.length === 5 && validName(playerName) && token)
      connect(roomCode, playerName, token);
  }, []);
  const createLobby = async () => {
    if (!validName(name)) return setError(tr.invalidName);
    const response = await fetch("/api/lobbies", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name: safeName(name), settings }),
    });
    const data = (await response.json()) as {
      code?: string;
      token?: string;
      error?: string;
    };
    if (!response.ok || !data.code)
      return setError(data.error ?? "Lobi oluşturulamadı.");
    localStorage.setItem("mm-name", safeName(name));
    setCode(data.code);
    connect(data.code, safeName(name), data.token);
  };
  const joinLobby = async (forcedCode?: string) => {
    if (!validName(name)) return setError(tr.invalidName);
    const targetCode = codeClean(forcedCode ?? code);
    if (targetCode.length !== 5) return setError(tr.invalidCode);
    setCode(targetCode);
    setError("");
    try {
      const check = await fetch(
        `/api/lobbies/${targetCode}?name=${encodeURIComponent(safeName(name))}`,
      );
      const status = (await check.json()) as {
        error?: string;
        players?: number;
        maxPlayers?: number;
        phase?: string;
        nameTaken?: boolean;
      };
      if (!check.ok) return setError(status.error ?? "Lobi bulunamadı.");
      const savedToken =
        localStorage.getItem(`mm-session-${targetCode}`) ?? undefined;
      if (status.nameTaken && !savedToken)
        return setError(
          "Bu isim lobide zaten kullanılıyor. Farklı bir oyuncu adı seç.",
        );
      if ((status.players ?? 0) >= (status.maxPlayers ?? 1))
        return setError("Bu lobi dolu.");
      if (status.phase !== "WAITING")
        return setError("Oyun başlamış; bu lobiye artık katılamazsın.");
    } catch {
      return setError(
        "Lobi kontrol edilemedi. İnternet bağlantını kontrol et.",
      );
    }
    localStorage.setItem("mm-name", safeName(name));
    connect(
      targetCode,
      safeName(name),
      localStorage.getItem(`mm-session-${targetCode}`) ?? undefined,
    );
  };
  const send = (payload: object) =>
    ws.current?.readyState === WebSocket.OPEN &&
    ws.current.send(JSON.stringify(payload));
  const leaveLobby = (nextScreen: Screen) => {
    if (!lobby) return setScreen(nextScreen);
    setPendingConfirm({ kind: "leave", nextScreen });
  };
  const closeLobby = () => {
    if (!lobby || !me?.isHost) return;
    setPendingConfirm({ kind: "close" });
  };
  const confirmPendingAction = () => {
    if (!pendingConfirm || !lobby) return setPendingConfirm(null);
    if (pendingConfirm.kind === "close") {
      send({ type: "CLOSE_LOBBY" });
      setPendingConfirm(null);
      return;
    }
    send({ type: "LEAVE" });
    localStorage.removeItem(`mm-session-${lobby.code}`);
    ws.current?.close();
    setLobby(null);
    setSelfId("");
    setScreen(pendingConfirm.nextScreen);
    const pageUrl = new URL(location.href);
    pageUrl.searchParams.delete("lobi");
    history.replaceState(null, "", pageUrl);
    setPendingConfirm(null);
  };

  return (
    <main className="app">
      <header className="nav">
        <button
          className="brand"
          onClick={() => leaveLobby("home")}
          aria-label="Ana sayfa"
        >
          <span className="brand-mark">M!</span>
          <span>{tr.brand}</span>
        </button>
        <div className="nav-actions">
          <button
            className={screen === "browse" ? "nav-lobbies active" : "nav-lobbies"}
            onClick={() => leaveLobby("browse")}
          >
            <Users /> Lobiler
          </button>
          <span className="live-pill">
            <i /> {liveCount} meydan canlı
          </span>
          <button
            className="icon-button"
            onClick={toggleSound}
            aria-label={sound ? "Sesi kapat" : "Sesi aç"}
          >
            {sound ? <Volume2 /> : <VolumeX />}
          </button>
        </div>
      </header>
      <AnimatePresence mode="wait">
        {screen === "home" && (
          <Home
            key="home"
            {...{
              name,
              setName,
              code,
              setCode,
              error,
              setError,
              joinLobby,
              setScreen,
            }}
          />
        )}
        {screen === "browse" && (
          <LobbyBrowser
            key="browse"
            rooms={publicLobbies}
            name={name}
            setName={setName}
            error={error}
            setError={setError}
            joinLobby={joinLobby}
            setScreen={setScreen}
          />
        )}
        {screen === "create" && (
          <Create
            key="create"
            {...{
              settings,
              setSettings,
              error,
              createLobby,
              setScreen,
              categories,
            }}
          />
        )}
        {screen === "lobby" &&
          lobby &&
          (lobby.phase === "WAITING" ? (
            <Lobby key="lobby" {...{ lobby, me, send }} />
          ) : (
            <Game
              key="game"
              lobby={lobby}
              send={send}
              soundEnabled={sound && lobby.settings.soundEffects}
              onMute={toggleSound}
            />
          ))}
      </AnimatePresence>
      {lobby && me?.isHost && (
        <button className="close-lobby-button" onClick={closeLobby}>
          {lobby.phase === "WAITING" ? "Lobiyi kapat" : "Oyunu bitir"}
        </button>
      )}
      {pendingConfirm && lobby && (
        <div
          className="confirm-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setPendingConfirm(null)}
        >
          <motion.div
            className="confirm-card"
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            onClick={(event) => event.stopPropagation()}
          >
            <span className={pendingConfirm.kind === "close" ? "confirm-icon danger" : "confirm-icon"}>
              {pendingConfirm.kind === "close" ? "!" : "←"}
            </span>
            <small>EMİN MİSİN?</small>
            <h2>
              {pendingConfirm.kind === "close"
                ? lobby.phase === "WAITING"
                  ? "Lobi kapatılsın mı?"
                  : "Oyun bitirilsin mi?"
                : "Oyundan çıkılsın mı?"}
            </h2>
            <p>
              {pendingConfirm.kind === "close"
                ? "Lobi kapanacak ve bütün oyuncular ana sayfaya gönderilecek."
                : "Bu lobiden ayrılacaksın. Daha sonra yeniden katılabilirsin."}
            </p>
            <div>
              <button className="confirm-cancel" onClick={() => setPendingConfirm(null)}>
                Hayır, kal
              </button>
              <button
                className={pendingConfirm.kind === "close" ? "confirm-accept danger" : "confirm-accept"}
                onClick={confirmPendingAction}
              >
                {pendingConfirm.kind === "close" ? "Evet, kapat" : "Evet, çık"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
      {screen === "home" && (
        <footer>
          <span>© 2026 Meme Meydanı</span>
          <div>
            <a href="/gizlilik">Gizlilik</a>
            <a href="/kosullar">Kullanım Şartları</a>
          </div>
        </footer>
      )}
    </main>
  );
}

function Home({
  name,
  setName,
  code,
  setCode,
  error,
  setError,
  joinLobby,
  setScreen,
}: {
  name: string;
  setName: (v: string) => void;
  code: string;
  setCode: (v: string) => void;
  error: string;
  setError: (v: string) => void;
  joinLobby: (code?: string) => void;
  setScreen: (v: Screen) => void;
}) {
  const invited =
    new URLSearchParams(location.search).has("lobi") &&
    codeClean(code).length === 5;
  const chooseRandomName = async () => {
    setError("");
    try {
      const endpoint = invited
        ? `/api/lobbies/${codeClean(code)}?randomName=1`
        : "/api/player-name";
      const response = await fetch(endpoint);
      const data = (await response.json()) as {
        name?: string;
        suggestedName?: string;
        error?: string;
      };
      const nextName = data.suggestedName ?? data.name;
      if (!response.ok || !nextName)
        return setError(data.error ?? "Oyuncu adı bulunamadı.");
      setName(nextName);
    } catch {
      setError("Oyuncu adı alınamadı. Tekrar dene.");
    }
  };
  return (
    <motion.div
      className="page"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <section className="hero">
        <div className="hero-fx" aria-hidden="true">
          <i className="orb orb-a" />
          <i className="orb orb-b" />
          <i className="orb orb-c" />
          <span className="spark spark-a">✦</span>
          <span className="spark spark-b">✧</span>
          <span className="spark spark-c">✦</span>
        </div>
        <div className="hero-copy">
          <div className="eyebrow">
            <Sparkles size={15} />
            {tr.kicker}
          </div>
          <h1>
            {tr.headlineA}
            <br />
            <em>{tr.headlineB}</em>
          </h1>
          <p>{tr.subtitle}</p>
          <div className="trust-row">
            <span>
              <Check /> Üyelik yok
            </span>
            <span>
              <Check /> Tamamen ücretsiz
            </span>
            <span>
              <Check /> Türkçe
            </span>
          </div>
        </div>
        <motion.div
          className="play-card"
          initial={{ rotate: 2 }}
          animate={{ rotate: [2, 1.2, 2], y: [0, -7, 0] }}
          transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
        >
          <span className="card-shine" aria-hidden="true" />
          <div className="card-top">
            <span>
              <Gamepad2 /> {invited ? "DAVET LOBİSİ" : "ŞİMDİ OYNA"}
            </span>
            <span className="secure">
              <LockKeyhole /> Özel lobi
            </span>
          </div>
          {invited && (
            <div className="invite-banner">
              <span>Lobi kodu</span>
              <strong>{codeClean(code)}</strong>
              <small>
                Oyuncu adını yazıp doğrudan arkadaşının lobisine katıl.
              </small>
            </div>
          )}
          <label>{tr.playerName}</label>
          <div className="input-wrap">
            <input
              value={name}
              onChange={(e) => {
                setName(safeName(e.target.value));
                setError("");
              }}
              placeholder="Mesela: Çıtır Panda"
              maxLength={18}
            />
            <button
              aria-label={tr.randomName}
              onClick={() => void chooseRandomName()}
            >
              <Dices />
            </button>
          </div>
          <button
            className="primary"
            onClick={() => {
              if (!validName(name)) return setError(tr.invalidName);
              setError("");
              if (invited) void joinLobby();
              else setScreen("create");
            }}
          >
            {invited ? `${codeClean(code)} lobisine katıl` : tr.create}
            <ArrowRight />
          </button>
          {!invited && (
            <>
              <div className="or">
                <span />
                ya da
                <span />
              </div>
              <div className="join-row">
                <input
                  value={code}
                  onChange={(e) => setCode(codeClean(e.target.value))}
                  placeholder="K7M4Q"
                  aria-label={tr.roomCode}
                />
                <button onClick={() => void joinLobby()}>
                  {tr.join}
                  <Users />
                </button>
              </div>
            </>
          )}
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
        </motion.div>
        <div className="float-card c2">
          <span>1750+</span>
          <b>EFSANE MEME!</b>
        </div>
        <div className="hero-sticker sticker-lol" aria-hidden="true">😂</div>
        <div className="hero-sticker sticker-fire" aria-hidden="true">🔥</div>
      </section>
      <div className="meme-marquee" aria-label="Oyun özellikleri">
        <div>
          {[0,1].map(copy=><React.Fragment key={copy}>
            <span>⚡ ANINDA LOBİ</span><b>✦</b>
            <span>😂 SINIRSIZ KAHKAHA</span><b>✦</b>
            <span>🎭 1750+ EFSANE MEME</span><b>✦</b>
            <span>🏆 ARKADAŞINI TAHTTAN İNDİR</span><b>✦</b>
          </React.Fragment>)}
        </div>
      </div>
      <section className="how">
        <div className="how-fx" aria-hidden="true"><span>😂</span><span>⚡</span><span>🏆</span></div>
        <div className="section-title">
          <span><Sparkles/> NASIL OYNANIR?</span>
          <h2>{tr.howTitle}</h2>
          <p>Odayı kur, kahkahayı ateşle, meydanın efsanesi ol.</p>
        </div>
        <div className="steps">
          <i className="step-rail" aria-hidden="true"><b/></i>
          {tr.steps.map(([n, t, d],index) => (
            <motion.article key={n} initial={{opacity:0,y:28,rotate:index===1?1:-1}} whileInView={{opacity:1,y:0,rotate:0}} viewport={{once:true,amount:.35}} transition={{delay:index*.13,duration:.5}}>
              <span className="step-badge">{index===0?"DAVET":index===1?"YARAT": "ZAFER"}</span>
              <strong>{n}</strong>
              <div className="step-icon">
                {n === "01" ? (
                  <Users />
                ) : n === "02" ? (
                  <Gamepad2 />
                ) : (
                  <Sparkles />
                )}
              </div>
              <h3>{t}</h3>
              <p>{d}</p>
              <div className="step-energy" aria-hidden="true"><i/><i/><i/></div>
            </motion.article>
          ))}
        </div>
      </section>
    </motion.div>
  );
}

function LobbyBrowser({
  rooms,
  name,
  setName,
  error,
  setError,
  joinLobby,
  setScreen,
}: {
  rooms: PublicLobby[];
  name: string;
  setName: (value: string) => void;
  error: string;
  setError: (value: string) => void;
  joinLobby: (code?: string) => void;
  setScreen: (screen: Screen) => void;
}) {
  return (
    <motion.section
      className="lobby-browser page-narrow"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <div className="lobby-browser-fx" aria-hidden="true">
        <i className="lobby-orb one"/><i className="lobby-orb two"/><span>✦</span>
      </div>
      <button className="back" onClick={() => setScreen("home")}>
        ← Ana sayfa
      </button>
      <div className="lobby-browser-head">
        <div>
          <div className="eyebrow"><Users /> AÇIK MEYDANLAR</div>
          <h1>Bir oyuna katıl</h1>
          <p>Bekleyen oyunları gör, kategorini seç ve doğrudan içeri gir.</p>
          <div className="lobby-stats">
            <span><i/> {rooms.length} açık lobi</span>
            <span><Users/> {rooms.reduce((sum,room)=>sum+room.current_players,0)} oyuncu bekliyor</span>
          </div>
        </div>
        <button className="primary" onClick={() => setScreen("create")}>
          Yeni oyun kur <Plus />
        </button>
      </div>
      <div className="browser-name">
        <label>Oyuncu adın</label>
        <input
          value={name}
          maxLength={18}
          onChange={(event) => {
            setName(safeName(event.target.value));
            setError("");
          }}
        />
      </div>
      {error && <p className="error">{error}</p>}
      <div className="lobby-list">
        {!!rooms.length && (
          <div className="lobby-list-head">
            <span>Oyuncular</span>
            <span>Kategori</span>
            <span>Lobi kodu</span>
            <span />
          </div>
        )}
        {rooms.map((room,index) => (
          <motion.article key={room.code} initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} transition={{delay:Math.min(index*.05,.4)}}>
            <span className="room-live"><i/> CANLI</span>
            <strong className="lobby-count">
              <Users /> {room.current_players}/{room.max_players}
            </strong>
            <span className="lobby-category"><Sparkles/>{room.category_name}</span>
            <code data-label="LOBI">{room.code}</code>
            <button className="primary" onClick={() => void joinLobby(room.code)}>
              Lobiye katıl <ArrowRight/>
            </button>
            <span className="room-capacity" aria-label={`${room.current_players}/${room.max_players} dolu`}>
              <i style={{width:`${Math.min(100,(room.current_players/room.max_players)*100)}%`}}/>
            </span>
          </motion.article>
        ))}
        {!rooms.length && (
          <div className="public-empty">
            <span className="empty-party">🎉</span>
            <b>Meydan şu an sessiz...</b>
            <small>Yeni oyun kurarak ilk kahkahayı sen başlat.</small>
          </div>
        )}
      </div>
    </motion.section>
  );
}

function Create({
  settings,
  setSettings,
  error,
  createLobby,
  setScreen,
  categories,
}: {
  settings: LobbySettings;
  setSettings: (v: LobbySettings) => void;
  error: string;
  createLobby: () => void;
  setScreen: (v: Screen) => void;
  categories: Array<{ id: number; name: string; meme_count: number }>;
}) {
  const set = <K extends keyof LobbySettings>(k: K, v: LobbySettings[K]) =>
    setSettings({ ...settings, [k]: v });
  return (
    <motion.section
      className="setup page-narrow"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <button className="back" onClick={() => setScreen("home")}>
        ← {tr.back}
      </button>
      <div className="eyebrow">
        <Gamepad2 /> OYUN AYARLARI
      </div>
      <h1>{tr.createTitle}</h1>
      <p>Ritmi seç, arkadaşlarını çağır, gerisini kahkahaya bırak.</p>
      <div className="setting-grid">
        <Setting title={tr.rounds} value={`${settings.rounds} tur`}>
          <input
            type="range"
            min="1"
            max="8"
            value={settings.rounds}
            onChange={(e) => set("rounds", +e.target.value)}
          />
        </Setting>
        <Setting title={tr.captionTime} value={`${settings.captionSeconds} sn`}>
          <input
            type="range"
            min="30"
            max="180"
            step="15"
            value={settings.captionSeconds}
            onChange={(e) => set("captionSeconds", +e.target.value)}
          />
        </Setting>
        <Setting title={tr.players} value={`${settings.maxPlayers} kişi`}>
          <input
            type="range"
            min="2"
            max="16"
            value={settings.maxPlayers}
            onChange={(e) => set("maxPlayers", +e.target.value)}
          />
        </Setting>
        <Setting title="Meme dağıtımı" value="">
          <select
            value={settings.templateMode}
            onChange={(e) =>
              set(
                "templateMode",
                e.target.value as LobbySettings["templateMode"],
              )
            }
          >
            <option value="random">Rastgele karışık</option>
            <option value="same">Herkese aynı</option>
            <option value="different">Herkese farklı</option>
          </select>
        </Setting>
        <Setting
          title="Oyuncuya gelecek meme"
          value={`${settings.templateChoices} seçenek`}
        >
          <select
            value={settings.templateChoices}
            onChange={(e) => set("templateChoices", +e.target.value)}
          >
            {Array.from({ length: 20 }, (_, index) => index + 1).map(
              (count) => (
                <option key={count} value={count}>
                  {count} meme
                </option>
              ),
            )}
          </select>
        </Setting>
        <Setting title="Meme kategorisi" value="">
          <select
            value={settings.categoryId ?? ""}
            onChange={(e) =>
              set("categoryId", e.target.value ? +e.target.value : undefined)
            }
          >
            <option value="">Tüm kategoriler</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.meme_count})
              </option>
            ))}
          </select>
        </Setting>
        {(
          [
            ["profanityFilter", "Küfür filtresi"],
            ["earlyFinish", "Herkes hazırsa erken bitir"],
            ["lateJoin", "Geç katılım"],
            ["spectatorMode", "Seyirci modu"],
            ["isPrivate", "Gizli lobi — yalnız kod veya link ile giriş"],
            ["soundEffects", "Tur sonu sesleri çalsın"],
            ["adultSounds", "+18 seslere izin ver"],
          ] as const
        ).map(([k, label]) => (
          <label className={k==="isPrivate"?"toggle private-toggle":"toggle"} key={k}>
            <span>{label}</span>
            <input
              type="checkbox"
              checked={settings[k]}
              onChange={(e) => set(k, e.target.checked)}
            />
            <i />
          </label>
        ))}
      </div>
      {error && <p className="error">{error}</p>}
      <button className="primary wide" onClick={createLobby}>
        {tr.continue}
        <ArrowRight />
      </button>
    </motion.section>
  );
}
function Setting({
  title,
  value,
  children,
}: {
  title: string;
  value: string;
  children: React.ReactNode;
}) {
  return (
    <div className="setting">
      <div>
        <b>{title}</b>
        <span>{value}</span>
      </div>
      {children}
    </div>
  );
}

function Lobby({
  lobby,
  me,
  send,
}: {
  lobby: LobbyView;
  me: LobbyView["players"][number] | undefined;
  send: (v: object) => void;
}) {
  const invite = `${location.origin}?lobi=${lobby.code}`;
  const copy = (text: string) => navigator.clipboard.writeText(text);
  const [chatText, setChatText] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [talking, setTalking] = useState(false);
  const [micError, setMicError] = useState("");
  const peers = useRef(new Map<string, RTCPeerConnection>());
  const localVoice = useRef<MediaStream | null>(null);
  const ensurePeer = (playerId: string) => {
    const existing = peers.current.get(playerId);
    if (existing) return existing;
    const peer = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.cloudflare.com:3478" }],
    });
    localVoice.current?.getTracks().forEach((track) => peer.addTrack(track, localVoice.current!));
    peer.onicecandidate = ({ candidate }) => {
      if (candidate)
        send({ type: "VOICE_SIGNAL", targetId: playerId, data: { candidate: candidate.toJSON() } });
    };
    peer.ontrack = ({ streams }) => {
      const audio = new Audio();
      audio.srcObject = streams[0];
      audio.autoplay = true;
      void audio.play().catch(() => undefined);
    };
    peers.current.set(playerId, peer);
    return peer;
  };
  useEffect(() => {
    const receive = (raw: Event) => {
      const event = raw as CustomEvent<Extract<ServerEvent, { type: "VOICE_SIGNAL" }>>;
      const { fromId, data } = event.detail;
      void (async () => {
        const peer = ensurePeer(fromId);
        if (data.description) {
          await peer.setRemoteDescription(data.description);
          if (data.description.type === "offer") {
            const answer = await peer.createAnswer();
            await peer.setLocalDescription(answer);
            send({ type: "VOICE_SIGNAL", targetId: fromId, data: { description: answer } });
          }
        }
        if (data.candidate) await peer.addIceCandidate(data.candidate).catch(() => undefined);
      })();
    };
    window.addEventListener("mm-voice-signal", receive);
    return () => {
      window.removeEventListener("mm-voice-signal", receive);
      peers.current.forEach((peer) => peer.close());
      localVoice.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);
  const startTalking = async () => {
    try {
      setMicError("");
      localVoice.current ??= await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      localVoice.current.getAudioTracks().forEach((track) => (track.enabled = true));
      setTalking(true);
      for (const player of lobby.players.filter((p) => p.connected && p.id !== me?.id)) {
        const peer = ensurePeer(player.id);
        const currentTracks = new Set(peer.getSenders().map((sender) => sender.track));
        localVoice.current.getTracks().forEach((track) => {
          if (!currentTracks.has(track)) peer.addTrack(track, localVoice.current!);
        });
        const offer = await peer.createOffer();
        await peer.setLocalDescription(offer);
        send({ type: "VOICE_SIGNAL", targetId: player.id, data: { description: offer } });
      }
    } catch {
      setMicError("Mikrofon izni verilmedi.");
      setTalking(false);
    }
  };
  const stopTalking = () => {
    localVoice.current?.getAudioTracks().forEach((track) => (track.enabled = false));
    setTalking(false);
  };
  const sendChat = (event: React.FormEvent) => {
    event.preventDefault();
    const text = chatText.trim();
    if (!text) return;
    send({ type: "CHAT", text });
    setChatText("");
  };
  return (
    <motion.section
      className="lobby page-narrow"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="lobby-head">
        <div className="players-panel head-players">
          <div className="panel-title">
            <h2>Oyuncular</h2>
            <span>{lobby.players.filter((player) => player.connected).length}/{lobby.settings.maxPlayers}</span>
          </div>
          <div className="player-list">
            {lobby.players.map((player) => (
              <motion.div layout className="player" key={player.id}>
                <span className={`avatar a${player.avatar % 5}`}>
                  {player.name.slice(0, 1).toUpperCase()}
                </span>
                <b>
                  {player.name}
                  {player.isHost && <small> HOST</small>}
                </b>
                <span className={player.ready ? "status ready" : "status"}>
                  {player.ready ? "Hazır" : player.connected ? "Bekliyor" : "Bağlanıyor"}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
        <div className="room-code">
          <div>
            <small>LOBİ KODU</small>
            <strong>{lobby.code}</strong>
            <button onClick={() => copy(lobby.code)}>
              <Copy /> {tr.copy}
            </button>
          </div>
          <button className="room-qr-button" onClick={() => setQrOpen(true)} aria-label="QR kodunu aç">
            <QrCode />
            <span>QR</span>
          </button>
        </div>
      </div>
      {qrOpen && (
        <div className="qr-modal" role="dialog" aria-modal="true" onClick={() => setQrOpen(false)}>
          <div onClick={(event) => event.stopPropagation()}>
            <button className="qr-close" onClick={() => setQrOpen(false)}>×</button>
            <QRCodeSVG value={invite} size={240} bgColor="#ffffff" fgColor="#171225" />
            <h2>Telefonla katıl</h2>
            <p>Kamerayla kodu okut ve doğrudan bu lobiye gir.</p>
          </div>
        </div>
      )}
      <div className="mobile-lobby-tools">
        <button
          className={talking ? "active" : ""}
          onPointerDown={() => void startTalking()}
          onPointerUp={stopTalking}
          onPointerCancel={stopTalking}
          onPointerLeave={stopTalking}
        >
          {talking ? <Mic /> : <MicOff />}
          <span>{talking ? "Konuşuyor" : "Konuş"}</span>
        </button>
        <button
          className={chatOpen ? "active" : ""}
          onClick={() => setChatOpen((open) => !open)}
        >
          <MessageCircle />
          <span>Mesaj</span>
        </button>
        <button
          className={me?.ready ? "active ready" : ""}
          onClick={() => send({ type: "READY", ready: !me?.ready })}
        >
          <Check />
          <span>{me?.ready ? "Hazırım" : "Hazır ol"}</span>
        </button>
      </div>
      {chatOpen && (
        <section className="mobile-chat lobby-chat">
          <div className="chat-messages">
            {(lobby.chatMessages ?? []).map((message) => (
              <p key={message.id} className={message.playerId === me?.id ? "mine" : ""}>
                <b>{message.playerName}</b>
                <span>{message.text}</span>
              </p>
            ))}
            {!lobby.chatMessages?.length && <small>Henüz mesaj yok. Bir selam çak.</small>}
          </div>
          <form onSubmit={sendChat}>
            <input value={chatText} maxLength={300} onChange={(e) => setChatText(e.target.value)} placeholder="Mesajını yaz…" />
            <button aria-label="Mesaj gönder"><Send /></button>
          </form>
        </section>
      )}
      <div className="lobby-grid">
        <aside>
          <button className="secondary" onClick={() => copy(invite)}>
            <Link2 /> {tr.invite}
          </button>
          <p className="invite-help">Davet bağlantısını paylaş; arkadaşın isim seçip doğrudan katılsın.</p>
          <div className="lobby-settings">
            <span>{lobby.settings.rounds} tur</span>
            <span>{lobby.settings.captionSeconds} sn</span>
            <span>
              {lobby.settings.templateMode === "same"
                ? "Aynı meme"
                : "Karışık meme"}
            </span>
            <span>{lobby.settings.templateChoices} meme seçeneği</span>
            {lobby.settings.isPrivate&&<span>🔒 Gizli lobi</span>}
          </div>
        </aside>
      </div>
      <div className="lobby-social desktop-lobby-social">
        <section className="lobby-chat">
          <div className="panel-title">
            <h2><MessageCircle /> Lobi sohbeti</h2>
          </div>
          <div className="chat-messages">
            {(lobby.chatMessages ?? []).map((message) => (
              <p key={message.id} className={message.playerId === me?.id ? "mine" : ""}>
                <b>{message.playerName}</b>
                <span>{message.text}</span>
              </p>
            ))}
            {!lobby.chatMessages?.length && <small>Henüz mesaj yok. Bir selam çak.</small>}
          </div>
          <form onSubmit={sendChat}>
            <input value={chatText} maxLength={300} onChange={(e) => setChatText(e.target.value)} placeholder="Mesajını yaz…" />
            <button aria-label="Mesaj gönder"><Send /></button>
          </form>
        </section>
        <section className="voice-panel">
          <Mic />
          <h2>Bas ve konuş</h2>
          <p>Butona basılı tuttuğun sürece lobidekiler seni duyar.</p>
          <button
            className={talking ? "talk-button active" : "talk-button"}
            onPointerDown={() => void startTalking()}
            onPointerUp={stopTalking}
            onPointerCancel={stopTalking}
            onPointerLeave={stopTalking}
          >
            {talking ? <Mic /> : <MicOff />}
            {talking ? "Konuşuyorsun…" : "Basılı tut"}
          </button>
          {micError && <small className="error">{micError}</small>}
        </section>
      </div>
      <div className="lobby-actions">
        <button
          className={`${me?.ready ? "secondary" : "primary"} desktop-ready`}
          onClick={() => send({ type: "READY", ready: !me?.ready })}
        >
          {me?.ready ? (
            <>{tr.notReady}</>
          ) : (
            <>
              {tr.ready}
              <Check />
            </>
          )}
        </button>
        {me?.isHost && (
          <button
            className="primary"
            disabled={
              lobby.players.length < 2 ||
              !lobby.players.filter((p) => p.connected).every((p) => p.ready)
            }
            onClick={() => send({ type: "START" })}
          >
            {lobby.players.filter((p) => p.connected).every((p) => p.ready)
              ? tr.start
              : "Herkes bekleniyor"}
            <ArrowRight />
          </button>
        )}
      </div>
    </motion.section>
  );
}

type PublicZone = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  placeholder: string;
  max_chars: number;
  color: string;
  background_enabled: number;
  background_color: string;
  background_opacity: number;
  max_font_size: number;
  uppercase: number;
  required: number;
};
type PublicTemplate = {
  id: string;
  title: string;
  optimized_key: string;
  width: number;
  height: number;
  zones: PublicZone[];
};
function Game({
  lobby,
  send,
  soundEnabled,
  onMute,
}: {
  lobby: LobbyView;
  send: (v: object) => void;
  soundEnabled: boolean;
  onMute: () => void;
}) {
  const [now, setNow] = useState(Date.now()),
    [captions, setCaptions] = useState<string[]>([]),
    [submitted, setSubmitted] = useState(false),
    [done, setDone] = useState(false),
    [voteIndex, setVoteIndex] = useState(0),
    [choiceIndex, setChoiceIndex] = useState(0),
    [showcaseIndex, setShowcaseIndex] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const choices =
      lobby.assignedTemplates?.length
        ? lobby.assignedTemplates
        : lobby.assignedTemplate
          ? [lobby.assignedTemplate]
          : [],
    template = choices[Math.min(choiceIndex, Math.max(0, choices.length - 1))];
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    setChoiceIndex(0);
    setVoteIndex(0);
    setShowcaseIndex(0);
  }, [lobby.round]);
  useEffect(() => {
    setCaptions(Array(template?.zones.length ?? 1).fill(""));
    setSubmitted(false);
    setDone(false);
  }, [lobby.round, template?.id]);
  const showcase = lobby.roundShowcase ?? [],
    currentShowcase =
      showcase[Math.min(showcaseIndex, Math.max(0, showcase.length - 1))];
  useEffect(() => {
    audioRef.current?.pause();
    audioRef.current = null;
    if (lobby.phase !== "ROUND_RESULT" || !currentShowcase) return;
    if (soundEnabled && currentShowcase.soundKey) {
      const audio = new Audio(`/media/${currentShowcase.soundKey}`);
      audioRef.current = audio;
      void audio.play().catch(() => undefined);
    }
    const timer =
      showcaseIndex < showcase.length - 1
        ? window.setTimeout(() => setShowcaseIndex((i) => i + 1), 6000)
        : undefined;
    return () => {
      if (timer) window.clearTimeout(timer);
      audioRef.current?.pause();
    };
  }, [
    lobby.phase,
    currentShowcase?.soundKey,
    showcaseIndex,
    showcase.length,
    soundEnabled,
  ]);
  const seconds = Math.max(
    0,
    Math.ceil(((lobby.endsAt ?? now) - now) / 1000),
  );
  if (lobby.phase === "COUNTDOWN")
    return (
      <section className="game-stage countdown-screen">
        <div className="eyebrow">
          <Gamepad2 /> OYUN BAŞLIYOR
        </div>
        <motion.strong
          key={seconds}
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
        >
          {seconds || "Hadi!"}
        </motion.strong>
        <h1>Hazır mısın?</h1>
        <p>İlk aklına gelen genelde en komiğidir.</p>
      </section>
    );
  if (lobby.phase === "CAPTIONING")
    return (
      <section className="game-stage caption-screen">
        <header>
          <div>
            <div className="eyebrow">
              <Sparkles /> TUR {lobby.round}/{lobby.settings.rounds}
            </div>
            <h1>Memeni hazırla</h1>
          </div>
          <div className="game-timer">
            <Clock3 />
            <strong>{seconds}</strong>
            <span>saniye</span>
          </div>
        </header>
        {!template ? (
          <div className="game-empty">
            <Gamepad2 />
            <h2>Meme atanamadı</h2>
            <p>Bağlantıyı yenileyip tekrar dene.</p>
          </div>
        ) : (
          <div className="caption-layout">
            <div className="meme-choice-area">
              {choices.length > 1 && (
                <div className="meme-choice-nav">
                  <button
                    type="button"
                    aria-label="Önceki meme"
                    disabled={submitted}
                    onClick={() =>
                      setChoiceIndex(
                        (i) => (i - 1 + choices.length) % choices.length,
                      )
                    }
                  >
                    <ChevronLeft />
                  </button>
                  <strong>
                    {choiceIndex + 1} / {choices.length}
                  </strong>
                  <span>Bir meme seç</span>
                  <button
                    type="button"
                    aria-label="Sonraki meme"
                    disabled={submitted}
                    onClick={() =>
                      setChoiceIndex((i) => (i + 1) % choices.length)
                    }
                  >
                    <ChevronRight />
                  </button>
                </div>
              )}
              <MemeView template={template} captions={captions} />
            </div>
            <aside>
              {template.zones.map((z, i) => (
                <label key={z.id}>
                  <span>Yazı alanı {i + 1}</span>
                  <textarea
                    value={captions[i] ?? ""}
                    maxLength={z.max_chars}
                    placeholder={z.placeholder}
                    disabled={submitted}
                    onChange={(e) =>
                      setCaptions((v) =>
                        v.map((x, n) => (n === i ? e.target.value : x)),
                      )
                    }
                  />
                  <small>
                    {captions[i]?.length ?? 0}/{z.max_chars}
                  </small>
                </label>
              ))}
              {!submitted ? (
                <button
                  className="primary"
                  disabled={template.zones.some(
                    (z, i) => z.required && !captions[i]?.trim(),
                  )}
                  onClick={() => {
                    send({
                      type: "SUBMIT",
                      round: lobby.round,
                      templateId: template.id,
                      captions,
                    });
                    setSubmitted(true);
                  }}
                >
                  Memeyi gönder
                  <ArrowRight />
                </button>
              ) : (
                <button
                  className="done-button"
                  disabled={done}
                  onClick={() => {
                    send({ type: "DONE" });
                    setDone(true);
                  }}
                >
                  <Check />
                  {done ? "Tamamsın — diğerleri bekleniyor" : "Ben tamamım"}
                </button>
              )}{" "}
              {submitted && !done && (
                <p className="submitted-note">
                  Kontrol et; hazırsan “Ben tamamım” de.
                </p>
              )}
            </aside>
          </div>
        )}
      </section>
    );
  if (lobby.phase === "VOTING") {
    const entries = lobby.submissions ?? [],
      current = entries[voteIndex];
    return (
      <section className="game-stage voting-screen">
        <header>
          <div>
            <div className="eyebrow">
              <Sparkles /> OYLAMA BAŞLADI
            </div>
            <h1>
              {current
                ? `${voteIndex + 1}/${entries.length} meme`
                : "Oylar hazırlanıyor"}
            </h1>
          </div>
          <div className="game-timer">
            <Clock3 />
            <strong>{seconds}</strong>
            <span>saniye</span>
          </div>
        </header>
        {current ? (
          <div className="single-vote">
            <MemeView template={current.template} captions={current.captions} />
            <p>Meme sahibi sonuçta açıklanacak</p>
            <div className="vote-actions">
              <button
                className="vote-dislike"
                onClick={() => {
                  send({
                    type: "VOTE",
                    submissionId: current.submissionId,
                    value: "dislike",
                  });
                  setVoteIndex((v) => v + 1);
                }}
              >
                <span className="vote-emoji">👎</span>
                Beğenmedim<small>-50</small>
              </button>
              <button
                className="vote-meh"
                onClick={() => {
                  send({
                    type: "VOTE",
                    submissionId: current.submissionId,
                    value: "meh",
                  });
                  setVoteIndex((v) => v + 1);
                }}
              >
                <span className="vote-emoji">😐</span>
                Bähhh<small>0</small>
              </button>
              <button
                className="vote-like"
                onClick={() => {
                  send({
                    type: "VOTE",
                    submissionId: current.submissionId,
                    value: "like",
                  });
                  setVoteIndex((v) => v + 1);
                }}
              >
                <span className="vote-emoji">😂</span>
                Çok güldüm<small>+100</small>
              </button>
            </div>
          </div>
        ) : (
          <div className="game-empty">
            <Check />
            <h2>Oyların tamam</h2>
            <p>Diğer oyuncuların oy vermesi bekleniyor.</p>
          </div>
        )}
      </section>
    );
  }
  if (lobby.phase === "ROUND_RESULT") {
    const fallback = lobby.roundResults?.[0],
      shown = currentShowcase?.result ?? fallback,
      label =
        currentShowcase?.kind === "best"
          ? "😂 TURUN EN İYİSİ"
          : currentShowcase?.kind === "meh"
            ? "😐 TURUN BÄHHH'I"
            : "👎 TURUN EN KÖTÜSÜ";
    return (
      <section className="game-stage result-screen showcase-screen">
        <div className="showcase-top">
          <div className="eyebrow">{label}</div>
          {soundEnabled && (
            <button className="mute-showcase" onClick={onMute}>
              <VolumeX /> Sesi durdur
            </button>
          )}
        </div>
        {shown ? (
          <>
            <MemeView template={shown.template} captions={shown.captions} />
            <h2>{shown.ownerName}</h2>
            <div className="showcase-dots">
              {showcase.map((_, i) => (
                <i key={i} className={i === showcaseIndex ? "active" : ""} />
              ))}
            </div>
          </>
        ) : (
          <div className="game-empty">Sonuç hazırlanıyor…</div>
        )}
      </section>
    );
  }
  return (
    <section className="game-stage result-screen">
      <div className="eyebrow">
        <Trophy /> OYUNUN KAZANANI
      </div>
      <h1>{[...lobby.players].sort((a, b) => b.score - a.score)[0]?.name}!</h1>
      <Scoreboard players={lobby.players} />
      <button className="primary" onClick={() => (location.href = "/")}>
        Yeni oyun oluştur
        <ArrowRight />
      </button>
    </section>
  );
}
function zoneBackground(z: PublicZone) {
  if (!z.background_enabled) return undefined;
  const value = (z.background_color || "#000000").replace("#", ""),
    n = Number.parseInt(
      value.length === 3
        ? value
            .split("")
            .map((x) => x + x)
            .join("")
        : value,
      16,
    );
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${z.background_opacity ?? 0.85})`;
}
function AutoFitCaption({ zone, text }: { zone: PublicZone; text: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const fit = () => {
      let size = Math.max(10, zone.max_font_size || 64);
      element.style.fontSize = `${size}px`;
      while (
        size > 10 &&
        (element.scrollWidth > element.clientWidth + 1 ||
          element.scrollHeight > element.clientHeight + 1)
      ) {
        size -= 1;
        element.style.fontSize = `${size}px`;
      }
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text, zone.max_font_size, zone.width, zone.height]);
  return (
    <div
      ref={ref}
      className="caption-overlay"
      style={{
        left: `${zone.x * 100}%`,
        top: `${zone.y * 100}%`,
        width: `${zone.width * 100}%`,
        height: `${zone.height * 100}%`,
        color: zone.color,
        WebkitTextStroke: "0",
        backgroundColor: zoneBackground(zone),
      }}
    >
      {zone.uppercase ? text.toUpperCase() : text}
    </div>
  );
}
function MemeView({
  template,
  captions,
}: {
  template: PublicTemplate;
  captions: string[];
}) {
  return (
    <div className="meme-canvas">
      <img src={`/media/${template.optimized_key}`} alt={template.title} />
      {template.zones.map((z, i) => (
        <AutoFitCaption key={z.id} zone={z} text={captions[i] ?? ""} />
      ))}
    </div>
  );
}
function Scoreboard({ players }: { players: LobbyView["players"] }) {
  return (
    <div className="scoreboard">
      {[...players]
        .sort((a, b) => b.score - a.score)
        .map((p, i) => (
          <div key={p.id}>
            <strong>{i + 1}</strong>
            <span className={`avatar a${p.avatar % 5}`}>{p.name[0]}</span>
            <b>{p.name}</b>
            <em>{p.score} puan</em>
          </div>
        ))}
    </div>
  );
}
createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {location.pathname === "/memegirisi" ? <Admin /> : <App />}
  </React.StrictMode>,
);
